import { useState, useCallback } from 'react';

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/reverse';
const ZIP_CACHE_KEY = 'nonna_zip_cache';

interface CachedZip {
  zipCode: string;
  latitude: number;
  longitude: number;
  timestamp: number;
}

// Cache ZIP for 24 hours since it rarely changes
const CACHE_DURATION = 24 * 60 * 60 * 1000;

const getCachedZip = (lat: number, lon: number): string | null => {
  try {
    const cached = localStorage.getItem(ZIP_CACHE_KEY);
    if (!cached) return null;
    
    const parsed: CachedZip = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is still valid and coordinates are close enough
    // (within ~0.01 degrees, roughly 1km)
    if (
      now - parsed.timestamp < CACHE_DURATION &&
      Math.abs(parsed.latitude - lat) < 0.01 &&
      Math.abs(parsed.longitude - lon) < 0.01
    ) {
      return parsed.zipCode;
    }
    
    return null;
  } catch {
    return null;
  }
};

const setCachedZip = (zipCode: string, latitude: number, longitude: number) => {
  try {
    const cached: CachedZip = {
      zipCode,
      latitude,
      longitude,
      timestamp: Date.now(),
    };
    localStorage.setItem(ZIP_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
};

export const useReverseGeocode = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getZipFromCoords = useCallback(async (lat: number, lon: number): Promise<string | null> => {
    // Check cache first
    const cachedZip = getCachedZip(lat, lon);
    if (cachedZip) {
      return cachedZip;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${NOMINATIM_URL}?lat=${lat}&lon=${lon}&format=json&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'NonnaRecipes/1.0', // Required by Nominatim
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch location data');
      }

      const data = await response.json();
      const zipCode = data.address?.postcode || null;

      if (zipCode) {
        // Cache the result
        setCachedZip(zipCode, lat, lon);
      }

      setLoading(false);
      return zipCode;
    } catch (err) {
      console.error('Reverse geocode error:', err);
      setError('Failed to determine ZIP code from location');
      setLoading(false);
      return null;
    }
  }, []);

  const clearCache = useCallback(() => {
    localStorage.removeItem(ZIP_CACHE_KEY);
  }, []);

  return {
    getZipFromCoords,
    clearCache,
    loading,
    error,
  };
};
